import React, { useState } from 'react';
import { Camera, Crop, Trash2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';
import PhotoUploadModal from '../PhotoUploadModal';
import { PhotoResult } from '../../types/photo';
import {
  VEHICLE_PHOTO_ASPECT,
  VEHICLE_PHOTO_ASPECT_CLASS,
  imageUrlToDataUrl,
  vehiclePhotoActions,
  vehiclePhotoFile,
} from '../../utils/vehiclePhoto';
import { VehiclePhoto } from './VehiclePhoto';

/**
 * The vehicle photo slot, shared by both registration forms.
 *
 * A photo is FRAMED before it is saved — the same cropper a visitor's photo goes through, at a
 * landscape ratio, with face detection off because this is a car. The Cadastro form used to send
 * the operator's file untouched into a fixed banner, so a wide photo lost its roof and its number
 * plate; the Operacional form had already been fixed, and the two then disagreed about what the
 * same action does. One component so they cannot drift again.
 *
 * With a photo already there, clicking ASKS. The common correction is "the photo is fine, it is
 * just framed badly", and the only way to do that used to be finding the original file again.
 *
 * The owning form keeps the photo state: this component reports what the operator chose
 * (`onPhotoPicked` with the framed file and a preview to show immediately, `onRemove`) and never
 * uploads anything itself — the upload belongs to the save, next to the rest of the vehicle.
 *
 * See VehiclePhotoField.test.tsx.
 */

export interface VehiclePhotoFieldProps {
  /** What to show now: a stored URL, or the data URL of a photo framed in this session. */
  photoUrl: string | null;
  /** Names the uploaded file. The photo can be chosen before the plate is typed. */
  plate?: string;
  label?: string;
  darkMode?: boolean;
  disabled?: boolean;
  onPhotoPicked: (file: File, previewDataUrl: string) => void;
  onRemove: () => void;
}

export const VehiclePhotoField: React.FC<VehiclePhotoFieldProps> = ({
  photoUrl,
  plate,
  label = 'Foto do Veículo',
  darkMode = false,
  disabled = false,
  onPhotoPicked,
  onRemove,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  // Set when RE-framing: the stored photo, inlined so the editor opens ON it. Null = pick a file.
  const [recropSource, setRecropSource] = useState<string | null>(null);
  const [isLoadingRecrop, setIsLoadingRecrop] = useState(false);

  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textPrimary = darkMode ? 'text-gray-100' : 'text-gray-900';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const surface = darkMode ? 'bg-gray-900' : 'bg-white';

  const openSlot = () => {
    // A menu of one option is just a slower file picker, so an empty slot goes straight in.
    if (photoUrl) {
      setShowMenu(true);
      return;
    }
    setRecropSource(null);
    setShowCropper(true);
  };

  const handleRecrop = async () => {
    if (!photoUrl) return;
    setShowMenu(false);
    setIsLoadingRecrop(true);
    try {
      // Inlined FIRST: a canvas painted from a cross-origin image is tainted, and the crop would
      // fail at the very end with nothing to show for it.
      setRecropSource(await imageUrlToDataUrl(photoUrl));
      setShowCropper(true);
    } catch (err: any) {
      toast.error(err?.message || 'Não foi possível abrir a foto para recortar');
    } finally {
      setIsLoadingRecrop(false);
    }
  };

  // Escape closes the menu. A click on the backdrop always did; a keyboard user had no way out
  // of a modal overlay at all, and the handler has to be on the window — the overlay div is not
  // focused, so a key pressed over it never reaches a handler bound to the div itself.
  React.useEffect(() => {
    if (!showMenu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMenu(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showMenu]);

  const ACTION_COPY = {
    recrop: { label: 'Recortar foto atual', hint: 'Reenquadrar sem trocar a imagem', icon: Crop },
    upload: { label: 'Enviar nova foto', hint: 'Escolher outro arquivo', icon: Upload },
    remove: { label: 'Remover foto', hint: 'O veículo fica sem foto', icon: Trash2 },
  } as const;

  return (
    <div className="space-y-1">
      <label className={cn('block text-[10px] font-bold tracking-widest uppercase', textMuted)}>{label}</label>

      {/* The box holds the ratio the cropper frames to, and the photo is shown WHOLE inside it —
          letterboxed rather than re-cropped, so what the operator chose is what shows. */}
      <button
        type="button"
        disabled={disabled}
        onClick={openSlot}
        aria-label={photoUrl ? 'Alterar foto do veículo' : 'Adicionar foto do veículo'}
        className={cn(
          'group relative w-full rounded-xl border-2 overflow-hidden transition-colors disabled:opacity-50',
          VEHICLE_PHOTO_ASPECT_CLASS,
          darkMode ? 'bg-gray-800' : 'bg-gray-50',
          photoUrl
            ? border
            : darkMode
              ? 'border-dashed border-gray-700 hover:border-gray-500'
              : 'border-dashed border-gray-300 hover:border-gray-400',
        )}
      >
        {photoUrl ? (
          <>
            <VehiclePhoto src={photoUrl} alt="Foto do veículo" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="h-5 w-5 text-white" />
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 select-none">
            <Camera className={cn('h-5 w-5', textMuted)} />
            <span className={cn('text-[10px] font-bold tracking-[0.2em] uppercase', textMuted)}>SEM FOTO</span>
          </div>
        )}
      </button>

      {/* No separate "Remover" link beside the slot: the menu already offers it, and two
          affordances for one action is how the forms drifted apart in the first place. The slot
          is the single entry point, the way a person's photo works. */}

      {showMenu && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowMenu(false)}
          role="presentation"
        >
          <div
            className={cn('w-full max-w-xs rounded-2xl border shadow-2xl overflow-hidden', surface, border)}
            onClick={(e) => e.stopPropagation()}
          >
            <p className={cn('px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase border-b', textMuted, border)}>
              {label}
            </p>
            {vehiclePhotoActions(!!photoUrl).map((action) => {
              const option = ACTION_COPY[action];
              return (
                <button
                  key={action}
                  type="button"
                  disabled={isLoadingRecrop}
                  onClick={() => {
                    if (action === 'recrop') { handleRecrop(); return; }
                    setShowMenu(false);
                    if (action === 'upload') { setRecropSource(null); setShowCropper(true); return; }
                    onRemove();
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors disabled:opacity-50',
                    darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50',
                    action === 'remove' && 'text-red-500',
                  )}
                >
                  <option.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className={cn('block text-sm font-semibold', action === 'remove' ? undefined : textPrimary)}>
                      {option.label}
                    </span>
                    <span className={cn('block text-[11px]', textMuted)}>{option.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Mounted only while open: it reads OperationalSettingsContext on every render, which
          throws for any caller outside that provider. */}
      {showCropper && (
        <PhotoUploadModal
          open
          initialImage={recropSource ?? undefined}
          onClose={() => { setShowCropper(false); setRecropSource(null); }}
          onConfirm={(result: PhotoResult) => {
            // The cropper's own data URL: no blob URL to revoke, and it survives the modal closing.
            onPhotoPicked(vehiclePhotoFile(result, plate), result.base64);
            setShowCropper(false);
            setRecropSource(null);
          }}
          title={label}
          cropShape="rect"
          aspectRatio={VEHICLE_PHOTO_ASPECT}
          disableFaceDetection
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default VehiclePhotoField;
